import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Search,
    Zap,
    MessageSquare,
    Target,
    Star,
    X,
    Copy,
    Heart,
    Volume2,
    Pause,
    FileDown,
    Sparkles,
} from "lucide-react";
import Layout from "../components/Layout";
import api, { API_BASE } from "../lib/api";

const CATEGORIES = [
    { id: "all", label: "Tout", icon: Star, color: "#FFFFFF" },
    { id: "favorites", label: "Mes favoris", icon: Heart, color: "#EE722E" },
    { id: "pitch", label: "Pitch", icon: Zap, color: "#EE722E" },
    { id: "objection", label: "Objections", icon: MessageSquare, color: "#FCDB53" },
    { id: "closing", label: "Closing", icon: Target, color: "#F89F63" },
];

function categoryColor(cat) {
    if (cat === "pitch") return "#EE722E";
    if (cat === "objection") return "#FCDB53";
    return "#F89F63"; // closing
}

export default function Terrain() {
    const [cards, setCards] = useState([]);
    const [cat, setCat] = useState("all");
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);
    const [playingId, setPlayingId] = useState(null);
    const [loadingAudioId, setLoadingAudioId] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const playAudio = async (card) => {
        if (playingId === card.card_id && audioRef.current) {
            audioRef.current.pause();
            setPlayingId(null);
            return;
        }
        if (audioRef.current) {
            audioRef.current.pause();
            setPlayingId(null);
        }
        setLoadingAudioId(card.card_id);
        // Iter-27.30 — show toast immediately so the user knows something is happening
        // (the first call hits OpenAI TTS and can take 5-10s; subsequent calls are cached and instant).
        const loadingToastId = toast.loading("Génération audio en cours…", {
            description: "Lecture immédiate au prochain clic (mise en cache).",
        });
        try {
            const res = await fetch(`${API_BASE}/terrain/${card.card_id}/audio`, {
                credentials: "include",
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onended = () => setPlayingId(null);
            audio.onpause = () => {
                if (audio.currentTime >= audio.duration - 0.1) setPlayingId(null);
            };
            audioRef.current = audio;
            await audio.play();
            setPlayingId(card.card_id);
            toast.dismiss(loadingToastId);
        } catch {
            toast.dismiss(loadingToastId);
            toast.error("Audio indisponible");
        } finally {
            setLoadingAudioId(null);
        }
    };

    const reload = async () => {
        const r = await api.get("/terrain");
        setCards(r.data);
    };

    useEffect(() => {
        reload();
    }, []);

    const toggleFavorite = async (card) => {
        try {
            if (card.is_favorite) {
                await api.delete(`/terrain/favorites/${card.card_id}`);
            } else {
                await api.post(`/terrain/favorites/${card.card_id}`);
            }
            setCards((prev) =>
                prev.map((c) =>
                    c.card_id === card.card_id ? { ...c, is_favorite: !c.is_favorite } : c,
                ),
            );
            if (active?.card_id === card.card_id) {
                setActive({ ...active, is_favorite: !active.is_favorite });
            }
        } catch {
            toast.error("Erreur");
        }
    };

    const copyContent = async (card) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(card.content);
            } else {
                // Fallback for non-secure contexts / older browsers
                const ta = document.createElement("textarea");
                ta.value = card.content;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            toast.success("Copié dans le presse-papier 📋");
        } catch {
            toast.error("Impossible de copier");
        }
    };

    const filtered = useMemo(() => {
        let res = cards;
        if (cat === "favorites") {
            res = res.filter((c) => c.is_favorite);
        } else if (cat !== "all") {
            res = res.filter((c) => c.category === cat);
        }
        if (q.trim()) {
            const lower = q.toLowerCase();
            res = res.filter(
                (c) =>
                    c.title.toLowerCase().includes(lower) ||
                    c.content.toLowerCase().includes(lower) ||
                    (c.tags || []).some((t) => t.toLowerCase().includes(lower)),
            );
        }
        return res;
    }, [cards, cat, q]);

    const favCount = cards.filter((c) => c.is_favorite).length;

    return (
        <Layout
            title="Terrain Rapide"
            subtitle="Consultation express sur le terrain"
        >
            {/* Hero */}
            <div className="relative rounded-3xl p-6 lg:p-8 mb-6 overflow-hidden bg-gradient-to-br from-shariti-orange to-shariti-orange-light text-white">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-shariti-yellow/40 blur-3xl" />
                <div className="relative z-10 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
                        <Zap className="w-7 h-7" fill="white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-thin tracking-[0.3em] uppercase opacity-80 mb-1">
                            Mode terrain
                        </p>
                        <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight">
                            Tout ce qu'il te faut, en 1 clic
                        </h2>
                    </div>
                    {favCount > 0 && (
                        <div className="hidden sm:flex items-center gap-1.5 bg-shariti-black/30 backdrop-blur rounded-2xl px-3 py-2">
                            <Heart className="w-4 h-4" fill="white" />
                            <span className="text-sm font-bold">{favCount}</span>
                        </div>
                    )}
                </div>
                {/* Iter-27.17 — Fiche mémo téléchargeable */}
                <div className="relative z-10 mt-5 flex flex-wrap gap-2.5 items-center">
                    <a
                        href={`${API_BASE}/terrain/memo`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="terrain-memo-download"
                        className="inline-flex items-center gap-2 bg-shariti-black/30 hover:bg-shariti-black/40 backdrop-blur rounded-2xl px-4 py-2.5 text-sm font-bold transition-all border border-white/15"
                    >
                        <FileDown className="w-4 h-4" />
                        Télécharger la fiche mémo (PDF)
                    </a>
                    <span className="text-[11px] text-white/65 font-light">
                        2 pages mobile · Script + Vigilance · à imprimer ou garder sur ton tél.
                    </span>
                </div>
            </div>

            {/* Search */}
            <div className="glass-card rounded-2xl p-1 mb-4 flex items-center" data-testid="terrain-search">
                <Search className="w-5 h-5 text-white/40 ml-4" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cherche un pitch, objection..."
                    className="flex-1 bg-transparent px-3 py-3 text-white placeholder-white/30 outline-none"
                    data-testid="terrain-search-input"
                />
                {q && (
                    <button
                        onClick={() => setQ("")}
                        className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-white/5 mr-2"
                    >
                        <X className="w-4 h-4 text-white/50" />
                    </button>
                )}
            </div>

            {/* Categories */}
            <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide pb-1" data-testid="terrain-categories">
                {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    return (
                        <button
                            key={c.id}
                            onClick={() => setCat(c.id)}
                            data-testid={`terrain-cat-${c.id}`}
                            className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-bold whitespace-nowrap transition-all ${
                                cat === c.id
                                    ? "bg-shariti-orange text-white"
                                    : "glass-card text-white/60 hover:text-white"
                            }`}
                        >
                            <Icon
                                className="w-4 h-4"
                                fill={c.id === "favorites" && cat === c.id ? "white" : "transparent"}
                            />
                            {c.label}
                            {c.id === "favorites" && favCount > 0 && (
                                <span className="text-[10px] bg-shariti-black/30 px-1.5 py-0.5 rounded-full">
                                    {favCount}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((card, i) => (
                    <motion.div
                        key={card.card_id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.03 }}
                        className="glass-card glass-card-hover rounded-3xl p-5 transition-all relative group"
                        data-testid={`terrain-card-${card.card_id}`}
                    >
                        {/* Favorite & Copy quick actions */}
                        <div className="absolute top-4 right-4 flex gap-1 z-10">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    playAudio(card);
                                }}
                                data-testid={`terrain-audio-${card.card_id}`}
                                disabled={loadingAudioId === card.card_id}
                                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                                    playingId === card.card_id
                                        ? "bg-shariti-yellow text-shariti-black"
                                        : "bg-white/5 text-white/60 hover:bg-shariti-yellow/30"
                                }`}
                                title={
                                    playingId === card.card_id
                                        ? "Pause"
                                        : "Écouter le pitch"
                                }
                            >
                                {loadingAudioId === card.card_id ? (
                                    <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                ) : playingId === card.card_id ? (
                                    <Pause className="w-4 h-4 fill-current" />
                                ) : (
                                    <Volume2 className="w-4 h-4" />
                                )}
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(card);
                                }}
                                data-testid={`terrain-fav-${card.card_id}`}
                                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                                    card.is_favorite
                                        ? "bg-shariti-orange text-white"
                                        : "bg-white/5 text-white/60 hover:bg-white/10"
                                }`}
                                title={card.is_favorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                            >
                                <Heart
                                    className="w-4 h-4"
                                    fill={card.is_favorite ? "white" : "transparent"}
                                />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyContent(card);
                                }}
                                data-testid={`terrain-copy-${card.card_id}`}
                                className="w-9 h-9 rounded-xl bg-white/5 text-white/60 flex items-center justify-center hover:bg-shariti-orange hover:text-white transition-all"
                                title="Copier le contenu"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>

                        <button
                            onClick={() => setActive(card)}
                            className="w-full text-left"
                        >
                            <div className="flex items-center gap-2 mb-3 pr-32">
                                <span
                                    className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full"
                                    style={{
                                        background: categoryColor(card.category),
                                        color: "#181818",
                                    }}
                                >
                                    {card.category}
                                </span>
                            </div>
                            <h3 className="text-lg font-extrabold text-white tracking-tight mb-2">
                                {card.title}
                            </h3>
                            <p className="text-sm text-white/60 font-light line-clamp-3">
                                {card.content}
                            </p>
                            {card.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-3">
                                    {card.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-[10px] text-shariti-orange-light font-bold uppercase tracking-widest"
                                        >
                                            #{t}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </button>
                    </motion.div>
                ))}
            </div>

            {filtered.length === 0 && (
                <div className="glass-card rounded-3xl p-10 text-center">
                    <p className="text-white/50">
                        {cat === "favorites"
                            ? "Aucun favori pour l'instant. Ajoute-en avec le ❤️ sur les cartes !"
                            : "Aucun résultat"}
                    </p>
                </div>
            )}

            {/* Detail modal */}
            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActive(null)}
                        className="fixed inset-0 z-50 bg-shariti-black/80 backdrop-blur flex items-end sm:items-center justify-center p-4"
                        data-testid="terrain-modal"
                    >
                        <motion.div
                            initial={{ y: 80, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 80, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-xl bg-shariti-surface rounded-3xl p-6 lg:p-8 border border-white/10 max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <span
                                    className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full"
                                    style={{
                                        background: categoryColor(active.category),
                                        color: "#181818",
                                    }}
                                >
                                    {active.category}
                                </span>
                                <button
                                    onClick={() => setActive(null)}
                                    data-testid="terrain-modal-close"
                                    className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10"
                                >
                                    <X className="w-4 h-4 text-white" />
                                </button>
                            </div>
                            <h3 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight mb-4">
                                {active.title}
                            </h3>
                            <p className="text-white/80 text-lg font-light leading-relaxed mb-6">
                                "{active.content}"
                            </p>
                            {active.coach_note && (
                                <div
                                    className="rounded-2xl border border-shariti-orange/30 bg-shariti-orange/8 p-4 mb-6"
                                    data-testid="terrain-coach-note"
                                >
                                    <p className="text-[10px] font-extrabold tracking-[0.25em] uppercase text-shariti-orange-light mb-2 inline-flex items-center gap-1.5">
                                        <Sparkles className="w-3 h-3" />
                                        Conseil terrain — coach
                                    </p>
                                    <p className="text-sm text-white/85 font-light leading-relaxed">
                                        {active.coach_note}
                                    </p>
                                </div>
                            )}
                            {active.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-6">
                                    {active.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-xs text-shariti-orange-light bg-shariti-orange/10 font-bold px-2.5 py-1 rounded-full"
                                        >
                                            #{t}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => playAudio(active)}
                                    data-testid="terrain-modal-audio"
                                    className={`inline-flex items-center justify-center gap-2 font-extrabold py-3 px-4 rounded-2xl transition-all ${
                                        playingId === active.card_id
                                            ? "bg-shariti-yellow text-shariti-black"
                                            : "glass-card text-white hover:border-shariti-yellow/40"
                                    }`}
                                >
                                    {playingId === active.card_id ? (
                                        <Pause className="w-4 h-4 fill-current" />
                                    ) : (
                                        <Volume2 className="w-4 h-4" />
                                    )}
                                    {playingId === active.card_id ? "Pause" : "Écouter"}
                                </button>
                                <button
                                    onClick={() => copyContent(active)}
                                    data-testid="terrain-modal-copy"
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copier
                                </button>
                                <button
                                    onClick={() => toggleFavorite(active)}
                                    data-testid="terrain-modal-fav"
                                    className={`inline-flex items-center justify-center gap-2 font-extrabold py-3 px-4 rounded-2xl transition-all ${
                                        active.is_favorite
                                            ? "bg-shariti-yellow text-shariti-black"
                                            : "glass-card text-white hover:border-shariti-orange/40"
                                    }`}
                                >
                                    <Heart
                                        className="w-4 h-4"
                                        fill={active.is_favorite ? "currentColor" : "transparent"}
                                    />
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Layout>
    );
}
